/**
 * NWS (National Weather Service) weather alerts data source.
 * Fetches active severe weather alerts across the US.
 * Free, no API key required. Public domain.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity } from '../base-source';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'weather';

function nwsSeverityToSeverity(nwsSeverity: string | undefined): Severity {
  switch (nwsSeverity?.toLowerCase()) {
    case 'extreme': return 'critical';
    case 'severe': return 'high';
    case 'moderate': return 'medium';
    case 'minor': return 'low';
    default: return 'info';
  }
}

function urgencyTag(urgency: string | undefined): string | undefined {
  if (!urgency) return undefined;
  const lower = urgency.toLowerCase();
  if (lower === 'immediate' || lower === 'expected') return `urgency-${lower}`;
  return undefined;
}

interface NwsAlertProperties {
  id: string;
  event: string;
  headline?: string;
  description?: string;
  severity?: string;
  certainty?: string;
  urgency?: string;
  effective?: string;
  expires?: string;
  senderName?: string;
  areaDesc?: string;
  category?: string;
}

interface NwsAlertFeature {
  id: string;
  properties: NwsAlertProperties;
  geometry?: {
    type: string;
    coordinates: number[] | number[][] | number[][][];
  } | null;
}

interface NwsAlertsResponse {
  features?: NwsAlertFeature[];
}

/**
 * Extract a representative lat/lng from GeoJSON geometry.
 * NWS alerts can be Points, Polygons, or MultiPolygons.
 */
function extractCentroid(geometry: NwsAlertFeature['geometry']): { lat: number; lng: number } | null {
  if (!geometry || !geometry.coordinates) return null;

  const coords = geometry.coordinates;

  if (geometry.type === 'Point') {
    const [lng, lat] = coords as number[];
    if (isFinite(lat) && isFinite(lng)) return { lat, lng };
    return null;
  }

  if (geometry.type === 'Polygon') {
    // Average exterior ring coordinates
    const ring = (coords as number[][][])[0];
    if (!ring || ring.length === 0) return null;
    let sumLat = 0;
    let sumLng = 0;
    for (const point of ring) {
      sumLng += point[0];
      sumLat += point[1];
    }
    return { lat: sumLat / ring.length, lng: sumLng / ring.length };
  }

  if (geometry.type === 'MultiPolygon') {
    // Use first polygon's first ring
    const firstPoly = (coords as unknown as number[][][][])[0];
    if (!firstPoly || !firstPoly[0] || firstPoly[0].length === 0) return null;
    const ring = firstPoly[0];
    let sumLat = 0;
    let sumLng = 0;
    for (const point of ring) {
      sumLng += point[0];
      sumLat += point[1];
    }
    return { lat: sumLat / ring.length, lng: sumLng / ring.length };
  }

  return null;
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const weatherSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'NWS Weather Alerts',
    description: 'US National Weather Service severe weather alerts',
    icon: 'cloud-rain',
    category: 'environmental',
    updateInterval: 300_000, // 5 min
    requiresApiKey: false,
    dataLicense: 'Public Domain',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] NWS weather alerts source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      const url = `${getApiBase()}/api/data/weather`;
      const response = await fetch(url, { signal: AbortSignal.timeout(15_000) });

      if (!response.ok) {
        throw new Error(`NWS API returned ${response.status}`);
      }

      const data: NwsAlertsResponse = await response.json();

      if (!data.features || !Array.isArray(data.features)) {
        return [];
      }

      const events: NormalizedEvent[] = [];

      for (const feature of data.features) {
        const props = feature.properties;
        if (!props.event) continue;

        const severity = nwsSeverityToSeverity(props.severity);
        const timestamp = props.effective ?? new Date().toISOString();

        const tags: string[] = ['weather', 'nws'];
        if (props.category) tags.push(props.category.toLowerCase());
        const uTag = urgencyTag(props.urgency);
        if (uTag) tags.push(uTag);

        const event: NormalizedEvent = {
          id: `nws-${props.id}`,
          source: SOURCE_ID,
          timestamp,
          type: 'weather',
          title: props.headline ?? props.event,
          description: props.areaDesc,
          severity,
          tags,
          links: [],
          raw: {
            nwsId: props.id,
            event: props.event,
            certainty: props.certainty,
            urgency: props.urgency,
            severity: props.severity,
            expires: props.expires,
            senderName: props.senderName,
          },
        };

        // Extract location from GeoJSON geometry
        const centroid = extractCentroid(feature.geometry);
        if (centroid) {
          event.location = {
            lat: centroid.lat,
            lng: centroid.lng,
            country: 'United States',
            countryCode: 'US',
            name: props.areaDesc,
          };
        }

        events.push(event);
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
